import { makeArticle } from "./learnFactory";

export const pageAnxietyWorseInMorning = makeArticle({
  slug: "why-is-my-anxiety-worse-in-the-morning",
  title: "Why Is My Anxiety Worse in the Morning? | Neuro Wellness",
  description:
    "Waking up anxious is common. Learn how morning cortisol, sleep, caffeine and depression play a role, plus simple steps to calm morning anxiety.",
  canonical: "/learn/why-is-my-anxiety-worse-in-the-morning",
  h1: "Why Is My Anxiety Worse in the Morning? Causes and How to Calm It",
  breadcrumbLabel: "Morning Anxiety",
  shortAnswer:
    "Morning anxiety is common, and there's a biological reason for it. Your body releases a surge of the stress hormone cortisol shortly after you wake up, and in people prone to anxiety that surge can feel like dread, a racing heart or a churning stomach. Poor sleep, caffeine on an empty stomach, low blood sugar, alcohol the night before and checking your phone first thing make it worse. Mood that is consistently worst in the morning can also be a sign of depression.",
  keyTakeaways: [
    "A natural cortisol surge after waking can feel like anxiety in a sensitive nervous system.",
    "Poor sleep, caffeine before food and alcohol the night before make morning anxiety worse.",
    "Feeling worst in the morning, with early waking and low mood, can point to depression.",
    "Small changes to the first 30 minutes of your day often help.",
  ],
  sections: [
    {
      heading: "The science: why mornings hit harder",
      paragraphs: [
        "If you wake up with your heart already racing and your mind already listing everything that could go wrong, you're describing one of the most common anxiety patterns there is. Several things stack up in the morning:",
      ],
      list: [
        "**The cortisol awakening response.** Cortisol naturally rises sharply soon after waking to get your body moving. It's normal and healthy, but for an anxious nervous system, that hormonal jolt can feel like alarm.",
        "**Poor or broken sleep.** Short or restless sleep makes the brain's threat system more reactive the next morning.",
        "**Low blood sugar.** After a night without food, shakiness and a racing heart can feel like anxiety.",
        "**Caffeine on an empty stomach.** Coffee before food adds a stimulant on top of the cortisol surge.",
        "**Alcohol rebound.** Even a couple of drinks the night before can cause early waking and next-morning jitteriness.",
        "**The day ahead.** Mornings are when your brain previews everything you have to face. Without anything else to focus on, worry fills the space.",
      ],
    },
    {
      heading: "When morning anxiety is really depression",
      paragraphs: [
        "Feeling worst in the morning and a little better as the day goes on is a recognized pattern in some types of depression. Signs it may be more than anxiety:",
      ],
      list: [
        "Waking very early (3 to 5 a.m.) and being unable to fall back asleep",
        "Dread about the day combined with a heavy, flat or empty mood",
        "Loss of interest in things you used to enjoy",
        "Exhaustion, trouble concentrating, or feeling worthless or hopeless",
        "These symptoms lasting two weeks or more",
      ],
      paragraphsAfter: [
        "Anxiety and depression often occur together, and treating both matters. Learn more about being [[depressed and anxious at the same time|/depressed-and-anxious-at-the-same-time]].",
      ],
    },
    {
      heading: "6 ways to calm morning anxiety",
      paragraphs: [],
      list: [
        "**Don't reach for your phone first.** Give yourself 20 to 30 minutes before email, news or social media.",
        "**Breathe before you get up.** Take five slow breaths with a longer exhale than inhale while still in bed.",
        "**Eat something before coffee.** A small breakfast with protein steadies blood sugar. Have caffeine after food, and consider cutting the amount.",
        "**Get light and movement.** Open the curtains or step outside for 10 minutes. A short walk burns off some of the stress-hormone surge.",
        "**Park the worries at night.** Spend five minutes in the evening writing tomorrow's to-do list and any worries, so your brain has less to preview when you wake.",
        "**Keep a steady sleep schedule.** Same bedtime and wake time every day, and limit alcohol in the evening. If your mind races at night, see [[when your brain won't shut off|/brain-wont-shut-off]].",
      ],
    },
    {
      heading: "Treating the root cause",
      paragraphs: [
        "If morning anxiety shows up most days, it's worth treating, not just managing. Cognitive behavioral therapy is very effective for anxiety. Medication helps many people. Treating sleep problems often improves morning symptoms on its own.",
        "Neuro Wellness TMS Centers of America in Coral Springs, Florida offers [[anxiety evaluation and treatment|/services/anxiety]], [[psychotherapy|/services/psychotherapy]] and [[medication management|/services/medication-management]]. When morning dread comes with major depression that hasn't improved on antidepressants, we are also a [[Verified SAINT® Provider|/saint]], offering an accelerated, FDA-cleared, five-day TMS treatment.",
      ],
    },
    {
      heading: "What to do next",
      paragraphs: [
        "If you wake up anxious most days, or morning dread comes with low mood and early waking, talk with a clinician about both anxiety and depression.",
        "Our team offers a free consultation. Call 954-827-9793.",
      ],
      links: [
        { to: "/services/anxiety", text: "Get relief from anxiety" },
        { to: "/contactus", text: "Schedule a free consultation" },
      ],
    },
  ],
  faqs: [
    {
      question: "Why do I wake up with anxiety every morning?",
      answer:
        "Your body releases a surge of cortisol, a stress hormone, shortly after waking, and in people prone to anxiety it can feel like dread or panic. Poor sleep, low blood sugar, caffeine on an empty stomach and alcohol the night before can make it worse.",
    },
    {
      question: "Is morning anxiety a sign of depression?",
      answer:
        "It can be. Feeling worst in the morning and better as the day goes on is a pattern in some types of depression, especially with early waking, low mood and loss of interest lasting two weeks or more.",
    },
    {
      question: "How do I stop morning anxiety fast?",
      answer:
        "Slow breathing with a longer exhale, eating before coffee, skipping your phone for the first 20 to 30 minutes and getting outside light can all help quickly. If it happens most days, talk to a clinician.",
    },
    {
      question: "Does coffee make morning anxiety worse?",
      answer:
        "For many people, yes. Caffeine adds a stimulant on top of the natural morning cortisol rise, so having coffee after food or cutting back often helps.",
    },
    {
      question: "Can morning anxiety be treated?",
      answer:
        "Yes. Therapy, especially cognitive behavioral therapy, medication, better sleep habits and treating any underlying depression can all reduce or stop morning anxiety.",
    },
  ],
  sources: [
    {
      citation:
        "Clow A, Hucklebridge F, Stalder T, Evans P, Thorn L. The cortisol awakening response: more than a measure of HPA axis function. Neuroscience & Biobehavioral Reviews, 2010.",
      url: "https://pubmed.ncbi.nlm.nih.gov/20026350/",
    },
    {
      citation: "National Institute of Mental Health. Anxiety Disorders.",
      url: "https://www.nimh.nih.gov/health/topics/anxiety-disorders",
    },
    {
      citation: "National Institute of Mental Health. Depression.",
      url: "https://www.nimh.nih.gov/health/publications/depression",
    },
  ],
  relatedLinks: [
    {
      title: "Anxiety treatment",
      href: "/services/anxiety",
      description: "How we evaluate and treat anxiety in Coral Springs.",
    },
    {
      title: "Depression, anxiety and insomnia",
      href: "/depression-anxiety-insomnia",
      description: "When sleep, mood and worry feed each other.",
    },
    {
      title: "Anxiety chest tightness",
      href: "/learn/anxiety-chest-tightness-when-to-worry",
      description: "Why it happens and when to call 911.",
    },
    {
      title: "Can't stop crying for no reason?",
      href: "/learn/cant-stop-crying-for-no-reason",
      description: "What frequent crying can mean and when to get help.",
    },
    {
      title: "Why am I so tired all the time?",
      href: "/learn/always-tired-depression-fatigue",
      description: "How depression drains your energy, and what helps.",
    },
    {
      title: "Anxiety nausea",
      href: "/anxiety-nausea-why-it-happens-how-to-stop-it",
      description: "Why anxiety upsets your stomach and how to stop it.",
    },
  ],
});
